import React, { useEffect, useState } from 'react';
import { employeesAPI, timeRecordsAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Plus, Search, Edit, Trash2, UserPlus, Phone, Mail, MapPin, 
  Briefcase, Clock, DollarSign, Building, GraduationCap, 
  Key, Download, Upload, Users, Calendar, FileText, Link2, Unlink, UserX
} from 'lucide-react';
import { canEdit } from '@/lib/auth';
import { formatCPF, formatPhone } from '@/lib/utils';

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [filterTeacher, setFilterTeacher] = useState('all');
  const [filterActive, setFilterActive] = useState('active');
  
  // Payment table
  const [paymentTableDialogOpen, setPaymentTableDialogOpen] = useState(false);
  const [paymentTable, setPaymentTable] = useState([]);
  const [newPaymentEntry, setNewPaymentEntry] = useState({ lesson_type: '', value: 0 });
  
  // Reset password modal
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordEmployee, setResetPasswordEmployee] = useState(null);
  const [resetPasswordData, setResetPasswordData] = useState({ customPassword: '', sendEmail: true });
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Link user modal
  const [linkUserDialogOpen, setLinkUserDialogOpen] = useState(false);
  const [linkingEmployee, setLinkingEmployee] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [linkingUser, setLinkingUser] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Dados do Funcionário
    name: '',
    full_name: '',
    registration_number: '',
    rg: '',
    cpf: '',
    gender: '',
    birth_date: '',
    birth_city: '',
    marital_status: '',
    dependents: 0,
    photo_url: '',
    contract_type: 'clt',
    professor_type: '',
    is_teacher: false,
    is_attendant: false,
    active: true,
    role: '',
    
    // Endereço
    zip_code: '',
    address: '',
    address_number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    
    // Comunicação
    phone: '',
    cellphone: '',
    email: '',
    no_email: false,
    
    // Dados Profissionais
    payment_type: 'mensalista',
    hourly_rate: 0,
    monthly_salary: 0,
    professional_card: '',
    title: '',
    pis_pasep: '',
    admission_date: '',
    dismissal_date: '',
    resume: '',
    access_card_number: '',
    
    // Dados Financeiros
    bank_code: '',
    bank_name: '',
    bank_agency: '',
    bank_account: '',
    bank_account_type: '',
    pix_key: '',
    
    notes: ''
  });

  const contractTypes = [
    { value: 'clt', label: 'CLT' },
    { value: 'pj', label: 'PJ (Pessoa Jurídica)' },
    { value: 'experiencia', label: 'Contrato de Experiência' },
  ];

  const professorTypes = [
    { value: 'mensalista', label: 'Mensalista' },
    { value: 'horista', label: 'Horista' },
  ];

  const states = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  // Lista completa de cargos
  const defaultRoles = [
    'Administrador(a)',
    'Assessor(a)',
    'Assistente de Professor',
    'Auxiliar Administrativo',
    'Auxiliar de Classe',
    'Auxiliar de Coordenação',
    'Auxiliar de Limpeza',
    'Auxiliar de Serviços Gerais',
    'Auxiliar Pedagógico',
    'Coordenador(a) Pedagógico(a)',
    'Copeiro(a)',
    'Diretor(a)',
    'Diretor(a) Administrativa',
    'Diretor(a) Comercial',
    'Divulgador(a)',
    'Estagiário(a)',
    'Gerente',
    'Inspetor(a)',
    'Monitora',
    'Professor(a)',
    'Professor(a) de Biologia',
    'Professor(a) de Ciências',
    'Professor(a) de Educação Física',
    'Professor(a) de Filosofia',
    'Professor(a) de Física',
    'Professor(a) de Geografia',
    'Professor(a) de História',
    'Professor(a) de Inglês',
    'Professor(a) de Literatura',
    'Professor(a) de Matemática',
    'Professor(a) de Música',
    'Professor(a) de Português',
    'Professor(a) de Química',
    'Professor(a) de Reforço',
    'Professor(a) de Sociologia',
    'Professor(a) de Terra de Excelência',
    'Professor(a) Educação Infantil',
    'Professor(a) Ensino Fundamental I',
    'Professor(a) Ensino Fundamental II',
    'Professor(a) Polivalente',
    'Secretário(a)',
    'Segurança',
    'Supervisor(a)',
    'Zelador(a)'
  ];

  const [roles, setRoles] = useState(defaultRoles);
  const [newRole, setNewRole] = useState('');
  const [showAddRoleInput, setShowAddRoleInput] = useState(false);

  const titles = [
    'Ensino Médio', 'Técnico', 'Graduação', 'Pós-Graduação',
    'Mestrado', 'Doutorado', 'Pós-Doutorado'
  ];

  const genderOptions = ['Masculino', 'Feminino', 'Outro', 'Não Informado'];
  const maritalStatusOptions = ['Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável'];
  const accountTypes = ['Corrente', 'Poupança'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await employeesAPI.list();
      setEmployees(response.data);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.full_name) {
      toast.error('Nome é obrigatório');
      return;
    }
    
    try {
      if (editingEmployee) {
        await employeesAPI.update(editingEmployee.id, formData);
        toast.success('Funcionário atualizado com sucesso');
      } else {
        await employeesAPI.create(formData);
        toast.success('Funcionário cadastrado com sucesso');
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar funcionário');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name || '',
      full_name: employee.full_name || '',
      registration_number: employee.registration_number || '',
      rg: employee.rg || '',
      cpf: employee.cpf || '',
      gender: employee.gender || '',
      birth_date: employee.birth_date || '',
      birth_city: employee.birth_city || '',
      marital_status: employee.marital_status || '',
      dependents: employee.dependents || 0,
      photo_url: employee.photo_url || '',
      contract_type: employee.contract_type || 'clt',
      professor_type: employee.professor_type || '',
      is_teacher: employee.is_teacher || false,
      is_attendant: employee.is_attendant || false,
      active: employee.active !== false,
      role: employee.role || '',
      zip_code: employee.zip_code || '',
      address: employee.address || '',
      address_number: employee.address_number || '',
      complement: employee.complement || '',
      neighborhood: employee.neighborhood || '',
      city: employee.city || '',
      state: employee.state || '',
      phone: employee.phone || '',
      cellphone: employee.cellphone || '',
      email: employee.email || '',
      no_email: employee.no_email || false,
      payment_type: employee.payment_type || 'mensalista',
      hourly_rate: employee.hourly_rate || 0,
      monthly_salary: employee.monthly_salary || 0,
      professional_card: employee.professional_card || '',
      title: employee.title || '',
      pis_pasep: employee.pis_pasep || '',
      admission_date: employee.admission_date || '',
      dismissal_date: employee.dismissal_date || '',
      resume: employee.resume || '',
      access_card_number: employee.access_card_number || '',
      bank_code: employee.bank_code || '',
      bank_name: employee.bank_name || '',
      bank_agency: employee.bank_agency || '',
      bank_account: employee.bank_account || '',
      bank_account_type: employee.bank_account_type || '',
      pix_key: employee.pix_key || '',
      notes: employee.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (employee) => {
    if (!window.confirm(`Deseja inativar ${employee.name}?`)) return;
    
    try {
      await employeesAPI.update(employee.id, { ...employee, active: false });
      toast.success('Funcionário inativado');
      loadData();
    } catch (error) {
      toast.error('Erro ao inativar');
    }
  };
  
  const handlePermanentDelete = async (employee) => {
    if (!window.confirm(`ATENÇÃO: Deseja EXCLUIR PERMANENTEMENTE ${employee.name}? Esta ação não pode ser desfeita.`)) return;
    
    try {
      await api.delete(`/employees/${employee.id}`);
      toast.success('Funcionário excluído permanentemente');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleCreateAccess = async (employee) => {
    if (!employee.email) {
      toast.error('Funcionário precisa ter email cadastrado');
      return;
    }
    
    try {
      const response = await employeesAPI.createAccess(employee.id);
      toast.success(
        <div>
          <p>Acesso criado com sucesso!</p>
          <p className="text-sm mt-1">Email: {response.data.email}</p>
          <p className="text-sm">Senha: {response.data.password}</p>
        </div>,
        { duration: 10000 }
      );
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar acesso');
    }
  };

  const handleResetPassword = async (employee, sendEmail = true) => {
    if (!employee.user_id) {
      toast.error('Funcionário não possui acesso ao sistema');
      return;
    }
    
    // Abrir modal
    setResetPasswordEmployee(employee);
    setResetPasswordData({ customPassword: '', sendEmail: true });
    setResetPasswordDialogOpen(true);
  };

  const executeResetPassword = async () => {
    if (!resetPasswordEmployee) return;
    
    setResettingPassword(true);
    try {
      const params = new URLSearchParams();
      params.append('send_email_flag', resetPasswordData.sendEmail);
      if (resetPasswordData.customPassword) {
        params.append('custom_password', resetPasswordData.customPassword);
      }
      
      const response = await api.post(`/employees/${resetPasswordEmployee.id}/reset-password?${params.toString()}`);
      
      toast.success(
        <div>
          <p>Senha alterada com sucesso!</p>
          <p className="text-sm mt-1">Email: {response.data.email}</p>
          <p className="text-sm">Nova Senha: <code className="bg-gray-100 px-2 py-0.5 rounded">{response.data.password}</code></p>
          {resetPasswordData.sendEmail && response.data.email_sent && (
            <p className="text-sm text-green-600 mt-1">✓ Email enviado com sucesso</p>
          )}
          {resetPasswordData.sendEmail && !response.data.email_sent && (
            <p className="text-sm text-yellow-600 mt-1">⚠ Falha ao enviar email</p>
          )}
        </div>,
        { duration: 15000 }
      );
      setResetPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    } finally {
      setResettingPassword(false);
    }
  };

  // Funções para vincular usuário
  const handleOpenLinkUser = async (employee) => {
    setLinkingEmployee(employee);
    setSelectedUserId(employee.user_id || '');
    setLinkUserDialogOpen(true);
    
    // Carregar usuários disponíveis
    try {
      const res = await api.get('/users');
      // Mostrar todos os usuários ativos (não filtrar por employee_id)
      // Permitir reatribuir se necessário
      const usersData = res.data.filter(u => u.active !== false);
      setAvailableUsers(usersData);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
      setAvailableUsers([]);
    }
  };

  const handleLinkUser = async () => {
    if (!linkingEmployee) return;
    
    setLinkingUser(true);
    try {
      await api.post(`/employees/${linkingEmployee.id}/link-user`, {
        user_id: selectedUserId || null
      });
      
      if (selectedUserId) {
        const user = availableUsers.find(u => u.id === selectedUserId);
        toast.success(`Funcionário vinculado ao usuário ${user?.email || ''}`);
      } else {
        toast.success('Funcionário desvinculado do usuário');
      }
      
      setLinkUserDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao vincular usuário');
    } finally {
      setLinkingUser(false);
    }
  };

  const handleOpenPaymentTable = async (employee) => {
    setEditingEmployee(employee);
    try {
      const response = await employeesAPI.getPaymentTable(employee.id);
      setPaymentTable(response.data);
      setPaymentTableDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar tabela de pagamento');
    }
  };

  const handleAddPaymentEntry = async () => {
    if (!newPaymentEntry.lesson_type) {
      toast.error('Tipo de aula é obrigatório');
      return;
    }
    
    try {
      await employeesAPI.addPaymentTableEntry(editingEmployee.id, newPaymentEntry);
      const response = await employeesAPI.getPaymentTable(editingEmployee.id);
      setPaymentTable(response.data);
      setNewPaymentEntry({ lesson_type: '', value: 0 });
      toast.success('Entrada adicionada');
    } catch (error) {
      toast.error('Erro ao adicionar entrada');
    }
  };

  const handleDeletePaymentEntry = async (entryId) => {
    try {
      await employeesAPI.deletePaymentTableEntry(entryId);
      const response = await employeesAPI.getPaymentTable(editingEmployee.id);
      setPaymentTable(response.data);
      toast.success('Entrada removida');
    } catch (error) {
      toast.error('Erro ao remover entrada');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/import/employees-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_funcionarios.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erro ao baixar template');
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/import/employees', formData);
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erros encontrados`);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar');
    }
    
    event.target.value = '';
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      name: '', full_name: '', registration_number: '', rg: '', cpf: '',
      gender: '', birth_date: '', birth_city: '', marital_status: '',
      dependents: 0, photo_url: '', contract_type: 'clt', professor_type: '',
      is_teacher: false, is_attendant: false,
      active: true, role: '', zip_code: '', address: '', address_number: '',
      complement: '', neighborhood: '', city: '', state: '', phone: '',
      cellphone: '', email: '', no_email: false, payment_type: 'mensalista',
      hourly_rate: 0, monthly_salary: 0, professional_card: '', title: '',
      pis_pasep: '', admission_date: '', dismissal_date: '', resume: '',
      access_card_number: '', bank_code: '', bank_name: '', bank_agency: '',
      bank_account: '', bank_account_type: '', pix_key: '', notes: ''
    });
  };

  const filteredEmployees = employees.filter(e => {
    const matchesSearch = e.name?.toLowerCase().includes(search.toLowerCase()) ||
                         e.full_name?.toLowerCase().includes(search.toLowerCase()) ||
                         e.cpf?.includes(search) ||
                         e.email?.toLowerCase().includes(search.toLowerCase());
    
    const matchesTeacher = filterTeacher === 'all' || 
                          (filterTeacher === 'teacher' && e.is_teacher) ||
                          (filterTeacher === 'other' && !e.is_teacher);
    
    const matchesActive = filterActive === 'all' ||
                         (filterActive === 'active' && e.active) ||
                         (filterActive === 'inactive' && !e.active);
    
    return matchesSearch && matchesTeacher && matchesActive;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="employees-title">Equipe DNA</h1>
          <p className="text-muted-foreground">Gerenciamento de professores e equipe escolar</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" />
            Template
          </Button>
          <label className="cursor-pointer">
            <Button variant="outline" size="sm" asChild>
              <span>
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </span>
            </Button>
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} />
          </label>
          {canEdit() && (
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-employee-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Funcionário
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Buscar por nome, CPF ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-employee"
              />
            </div>
            <Select value={filterTeacher} onValueChange={setFilterTeacher}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="teacher">Professores</SelectItem>
                <SelectItem value="other">Outros Funcionários</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{employees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <GraduationCap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Professores</p>
                <p className="text-2xl font-bold">{employees.filter(e => e.is_teacher).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Outros</p>
                <p className="text-2xl font-bold">{employees.filter(e => !e.is_teacher).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Key className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Com Acesso</p>
                <p className="text-2xl font-bold">{employees.filter(e => e.user_id).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employees List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium">Nome</th>
                  <th className="text-left p-4 font-medium">Cargo</th>
                  <th className="text-left p-4 font-medium">Contato</th>
                  <th className="text-left p-4 font-medium">Admissão</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-right p-4 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="border-t hover:bg-muted/30" data-testid={`employee-row-${employee.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-medium">
                          {employee.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{employee.name}</p>
                          <p className="text-sm text-muted-foreground">{employee.cpf || 'Sem CPF'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        <span>{employee.role || 'Não definido'}</span>
                        {employee.is_teacher && (
                          <Badge variant="secondary" className="w-fit">
                            <GraduationCap className="w-3 h-3 mr-1" />
                            Professor
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1 text-sm">
                        {employee.cellphone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {formatPhone(employee.cellphone)}
                          </span>
                        )}
                        {employee.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {employee.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-sm">
                      {employee.admission_date || '-'}
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        <Badge variant={employee.active ? 'default' : 'secondary'}>
                          {employee.active ? 'Ativo' : 'Inativo'}
                        </Badge>
                        {employee.user_id || employee.linked_user ? (
                          <Badge variant="outline" className="w-fit bg-green-50 text-green-700 border-green-200">
                            <Link2 className="w-3 h-3 mr-1" />
                            Acesso
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="w-fit text-gray-500">
                            <Unlink className="w-3 h-3 mr-1" />
                            Sem acesso
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        {employee.payment_type === 'horista' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenPaymentTable(employee)}
                            title="Tabela de Pagamento"
                          >
                            <DollarSign className="w-4 h-4" />
                          </Button>
                        )}
                        {canEdit() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenLinkUser(employee)}
                            title={employee.user_id ? "Gerenciar Vínculo com Usuário" : "Vincular a Usuário"}
                            className={employee.user_id ? "text-green-600 hover:text-green-700" : "text-blue-600 hover:text-blue-700"}
                          >
                            {employee.user_id ? <Link2 className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                          </Button>
                        )}
                        {!employee.user_id && employee.email && canEdit() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCreateAccess(employee)}
                            title="Criar Acesso (Nova Conta)"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        )}
                        {employee.user_id && canEdit() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResetPassword(employee, true)}
                            title="Resetar Senha e Enviar por Email"
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                        )}
                        {canEdit() && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(employee)}
                              data-testid={`edit-employee-${employee.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {employee.active !== false ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(employee)}
                                className="text-orange-600 hover:text-orange-700"
                                title="Inativar funcionário"
                                data-testid={`delete-employee-${employee.id}`}
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePermanentDelete(employee)}
                                className="text-destructive hover:text-destructive"
                                title="Excluir permanentemente"
                                data-testid={`permanent-delete-employee-${employee.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredEmployees.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">
                      Nenhum funcionário encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Employee Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="dados" className="w-full">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="dados">Dados</TabsTrigger>
                <TabsTrigger value="endereco">Endereço</TabsTrigger>
                <TabsTrigger value="profissional">Profissional</TabsTrigger>
                <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
                <TabsTrigger value="followup">Follow ups</TabsTrigger>
                <TabsTrigger value="obs">Obs/Anexos</TabsTrigger>
              </TabsList>

              {/* Tab: Dados do Funcionário */}
              <TabsContent value="dados" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome / Apelido *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="employee-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Nome Completo *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      data-testid="employee-full-name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="registration_number">Nº Matrícula</Label>
                    <Input
                      id="registration_number"
                      value={formData.registration_number}
                      onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="rg">R.G.</Label>
                    <Input
                      id="rg"
                      value={formData.rg}
                      onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cpf">C.P.F.</Label>
                    <Input
                      id="cpf"
                      value={formData.cpf}
                      onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                      data-testid="employee-cpf"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gênero</Label>
                    <Select value={formData.gender || 'none'} onValueChange={(v) => setFormData({ ...formData, gender: v === 'none' ? '' : v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Selecione</SelectItem>
                        {genderOptions.map(g => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="birth_date">Data Nasc.</Label>
                    <Input
                      id="birth_date"
                      type="date"
                      value={formData.birth_date}
                      onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="marital_status">Estado Civil</Label>
                    <Select value={formData.marital_status || 'none'} onValueChange={(v) => setFormData({ ...formData, marital_status: v === 'none' ? '' : v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Selecione</SelectItem>
                        {maritalStatusOptions.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dependents">Nº Dependentes</Label>
                    <Input
                      id="dependents"
                      type="number"
                      min="0"
                      value={formData.dependents}
                      onChange={(e) => setFormData({ ...formData, dependents: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="role">Cargo *</Label>
                    <div className="flex gap-2">
                      <Select value={formData.role || 'none'} onValueChange={(v) => {
                        if (v === 'add_new') {
                          setShowAddRoleInput(true);
                        } else {
                          setFormData({ ...formData, role: v === 'none' ? '' : v });
                        }
                      }}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          <SelectItem value="none">Selecione</SelectItem>
                          {roles.map(r => (
                            <SelectItem key={r} value={r}>{r}</SelectItem>
                          ))}
                          <SelectItem value="add_new" className="text-blue-600 font-medium">+ Adicionar novo cargo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {showAddRoleInput && (
                      <div className="flex gap-2 mt-2">
                        <Input
                          placeholder="Nome do novo cargo"
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            if (newRole.trim()) {
                              const updatedRoles = [...roles, newRole.trim()].sort();
                              setRoles(updatedRoles);
                              setFormData({ ...formData, role: newRole.trim() });
                              setNewRole('');
                              setShowAddRoleInput(false);
                              toast.success('Cargo adicionado!');
                            }
                          }}
                        >
                          Adicionar
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setNewRole('');
                            setShowAddRoleInput(false);
                          }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="birth_city">Cidade Natal</Label>
                    <Input
                      id="birth_city"
                      value={formData.birth_city}
                      onChange={(e) => setFormData({ ...formData, birth_city: e.target.value })}
                    />
                  </div>
                </div>

                <Separator />

                {/* Tipo de Contrato */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contract_type" className="font-semibold">Tipo de Contrato</Label>
                    <Select
                      value={formData.contract_type || 'clt'}
                      onValueChange={(value) => setFormData({ ...formData, contract_type: value })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione o tipo de contrato" />
                      </SelectTrigger>
                      <SelectContent>
                        {contractTypes.map((ct) => (
                          <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {formData.contract_type === 'clt' && 'CLT: Com INSS, IRRF e FGTS'}
                      {formData.contract_type === 'pj' && 'PJ: Sem encargos trabalhistas'}
                      {formData.contract_type === 'experiencia' && 'Experiência: Sem encargos (período de teste)'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="professor_type" className="font-semibold">Tipo de Professor (se aplicável)</Label>
                    <Select
                      value={formData.professor_type || 'nenhum'}
                      onValueChange={(value) => setFormData({ ...formData, professor_type: value === 'nenhum' ? '' : value })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nenhum">Não é professor</SelectItem>
                        {professorTypes.map((pt) => (
                          <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {formData.professor_type === 'mensalista' && 'Mensalista: Sal.Base + Hora-Atividade (5%)'}
                      {formData.professor_type === 'horista' && 'Horista: Sal.Base + H.Ativ (5%) + DSR (1/6)'}
                    </p>
                  </div>
                </div>

                <div className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_teacher"
                      checked={formData.is_teacher}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_teacher: checked })}
                    />
                    <Label htmlFor="is_teacher" className="cursor-pointer">
                      Professor (Marque esta opção se o funcionário for professor)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_attendant"
                      checked={formData.is_attendant}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_attendant: checked })}
                    />
                    <Label htmlFor="is_attendant" className="cursor-pointer">
                      Atendente (Marque se usar o controle de follow ups)
                    </Label>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                  <Label htmlFor="active" className="cursor-pointer">Ativo</Label>
                </div>
              </TabsContent>

              {/* Tab: Endereço/Comunicação */}
              <TabsContent value="endereco" className="space-y-4 mt-4">
                <h3 className="font-semibold text-lg">Endereço</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">C.E.P.</Label>
                    <Input
                      id="zip_code"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">Rua/Avenida</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address_number">Nº</Label>
                    <Input
                      id="address_number"
                      value={formData.address_number}
                      onChange={(e) => setFormData({ ...formData, address_number: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="complement">Complemento</Label>
                    <Input
                      id="complement"
                      value={formData.complement}
                      onChange={(e) => setFormData({ ...formData, complement: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="neighborhood">Bairro</Label>
                    <Input
                      id="neighborhood"
                      value={formData.neighborhood}
                      onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">Cidade</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state">UF</Label>
                    <Select value={formData.state || 'none'} onValueChange={(v) => setFormData({ ...formData, state: v === 'none' ? '' : v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Selecione</SelectItem>
                        {states.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                <h3 className="font-semibold text-lg">Comunicação</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Fone Residencial</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cellphone">Celular</Label>
                    <Input
                      id="cellphone"
                      value={formData.cellphone}
                      onChange={(e) => setFormData({ ...formData, cellphone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="employee-email"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="no_email"
                    checked={formData.no_email}
                    onCheckedChange={(checked) => setFormData({ ...formData, no_email: checked })}
                  />
                  <Label htmlFor="no_email" className="cursor-pointer">Não quero receber e-mail</Label>
                </div>
              </TabsContent>

              {/* Tab: Dados Profissionais */}
              <TabsContent value="profissional" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="monthly_salary">Salário Mensal (R$)</Label>
                    <Input
                      id="monthly_salary"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.monthly_salary}
                      onChange={(e) => setFormData({ ...formData, monthly_salary: parseFloat(e.target.value) || 0 })}
                      placeholder="Para mensalistas e funcionários"
                    />
                    <p className="text-xs text-muted-foreground">
                      Usado para professores mensalistas e funcionários CLT
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hourly_rate">Valor Hora-Aula (R$)</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                      placeholder="Para horistas"
                    />
                    <p className="text-xs text-muted-foreground">
                      Usado para professores horistas (Sal.Base = Nº Aulas × Vlr × 4,5)
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="professional_card">Carteira Profissional</Label>
                    <Input
                      id="professional_card"
                      value={formData.professional_card}
                      onChange={(e) => setFormData({ ...formData, professional_card: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="title">Titulação</Label>
                    <Select value={formData.title || 'none'} onValueChange={(v) => setFormData({ ...formData, title: v === 'none' ? '' : v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Selecione</SelectItem>
                        {titles.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pis_pasep">PIS/PASEP</Label>
                    <Input
                      id="pis_pasep"
                      value={formData.pis_pasep}
                      onChange={(e) => setFormData({ ...formData, pis_pasep: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admission_date">Data de Admissão</Label>
                    <Input
                      id="admission_date"
                      type="date"
                      value={formData.admission_date}
                      onChange={(e) => setFormData({ ...formData, admission_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dismissal_date">Data de Demissão</Label>
                    <Input
                      id="dismissal_date"
                      type="date"
                      value={formData.dismissal_date}
                      onChange={(e) => setFormData({ ...formData, dismissal_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="access_card_number">Nº do cartão para catraca</Label>
                  <Input
                    id="access_card_number"
                    value={formData.access_card_number}
                    onChange={(e) => setFormData({ ...formData, access_card_number: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="resume">Currículo</Label>
                  <Textarea
                    id="resume"
                    rows={4}
                    value={formData.resume}
                    onChange={(e) => setFormData({ ...formData, resume: e.target.value })}
                    placeholder="Resumo das qualificações..."
                  />
                </div>
              </TabsContent>

              {/* Tab: Dados Financeiros */}
              <TabsContent value="financeiro" className="space-y-4 mt-4">
                <h3 className="font-semibold text-lg">Dados Bancários</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_code">Código do Banco</Label>
                    <Input
                      id="bank_code"
                      value={formData.bank_code}
                      onChange={(e) => setFormData({ ...formData, bank_code: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="bank_name">Nome do Banco</Label>
                    <Input
                      id="bank_name"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank_agency">Agência</Label>
                    <Input
                      id="bank_agency"
                      value={formData.bank_agency}
                      onChange={(e) => setFormData({ ...formData, bank_agency: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account">Conta</Label>
                    <Input
                      id="bank_account"
                      value={formData.bank_account}
                      onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank_account_type">Tipo de Conta</Label>
                    <Select value={formData.bank_account_type || 'none'} onValueChange={(v) => setFormData({ ...formData, bank_account_type: v === 'none' ? '' : v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Selecione</SelectItem>
                        {accountTypes.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pix_key">Chave PIX</Label>
                  <Input
                    id="pix_key"
                    value={formData.pix_key}
                    onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })}
                    placeholder="CPF, Email, Telefone ou Chave Aleatória"
                  />
                </div>
              </TabsContent>

              {/* Tab: Follow ups */}
              <TabsContent value="followup" className="space-y-4 mt-4">
                <div className="text-center p-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Histórico de follow ups será exibido aqui após o cadastro.</p>
                </div>
              </TabsContent>

              {/* Tab: Obs/Anexos */}
              <TabsContent value="obs" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="notes">Observações</Label>
                  <Textarea
                    id="notes"
                    rows={6}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Observações gerais sobre o funcionário..."
                  />
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-employee-btn">
                {editingEmployee ? 'Salvar Alterações' : 'Cadastrar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Table Dialog */}
      <Dialog open={paymentTableDialogOpen} onOpenChange={setPaymentTableDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Tabela de Pagamento - {editingEmployee?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3">Tipo de Pagamento</th>
                    <th className="text-right p-3">Valor</th>
                    <th className="text-right p-3 w-20">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentTable.map((entry) => (
                    <tr key={entry.id} className="border-t">
                      <td className="p-3">{entry.lesson_type}</td>
                      <td className="p-3 text-right">R$ {entry.value?.toFixed(2)}</td>
                      <td className="p-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePaymentEntry(entry.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {paymentTable.length === 0 && (
                    <tr>
                      <td colSpan={3} className="p-4 text-center text-muted-foreground">
                        Nenhuma entrada cadastrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Separator />

            <div className="flex gap-2">
              <Input
                placeholder="Tipo (ex: Aulas Normais)"
                value={newPaymentEntry.lesson_type}
                onChange={(e) => setNewPaymentEntry({ ...newPaymentEntry, lesson_type: e.target.value })}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Valor"
                value={newPaymentEntry.value}
                onChange={(e) => setNewPaymentEntry({ ...newPaymentEntry, value: parseFloat(e.target.value) || 0 })}
                className="w-32"
              />
              <Button onClick={handleAddPaymentEntry}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Alterar Senha do Funcionário */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-600" />
              Alterar Senha
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {resetPasswordEmployee && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>Funcionário:</strong> {resetPasswordEmployee.name}</p>
                <p><strong>Email:</strong> {resetPasswordEmployee.email}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="customPasswordEmp">Nova Senha</Label>
              <Input
                id="customPasswordEmp"
                type="text"
                placeholder="Deixe em branco para gerar automaticamente"
                value={resetPasswordData.customPassword}
                onChange={(e) => setResetPasswordData(prev => ({ ...prev, customPassword: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Mínimo 6 caracteres. Se deixar em branco, será usada a senha padrão (CPF) ou gerada automaticamente.
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendEmailEmp"
                checked={resetPasswordData.sendEmail}
                onCheckedChange={(checked) => setResetPasswordData(prev => ({ ...prev, sendEmail: checked }))}
              />
              <Label htmlFor="sendEmailEmp" className="text-sm font-normal cursor-pointer">
                Enviar senha por email
              </Label>
            </div>
            
            {resetPasswordData.sendEmail && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <Mail className="w-4 h-4 inline mr-2" />
                O email incluirá o link de acesso ao sistema e as credenciais.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={executeResetPassword} 
              disabled={resettingPassword || (resetPasswordData.customPassword && resetPasswordData.customPassword.length < 6)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {resettingPassword ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Vincular Usuário */}
      <Dialog open={linkUserDialogOpen} onOpenChange={setLinkUserDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Vincular Usuário
            </DialogTitle>
            <DialogDescription>
              {linkingEmployee && (
                <>Vincular <strong>{linkingEmployee.name}</strong> a um usuário do sistema</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info atual */}
            {linkingEmployee?.user_id && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-xs text-green-600 font-medium mb-1">Usuário Vinculado Atualmente</p>
                <p className="text-sm font-semibold text-green-800">
                  {linkingEmployee.linked_user?.email || availableUsers.find(u => u.id === linkingEmployee.user_id)?.email || 'Carregando...'}
                </p>
              </div>
            )}
            
            {/* Select de usuário */}
            <div className="space-y-2">
              <Label>Selecionar Usuário</Label>
              <Select 
                value={selectedUserId || "none"} 
                onValueChange={(val) => setSelectedUserId(val === "none" ? "" : val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um usuário" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-gray-500">Nenhum (desvincular)</span>
                  </SelectItem>
                  {availableUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex flex-col">
                        <span>{user.name || user.email}</span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Info sobre sincronização */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs text-blue-800">
              <strong>Sincronização automática:</strong>
              <p className="mt-1">Ao vincular, os dados do funcionário (nome, email, telefone) serão sincronizados automaticamente com o usuário.</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setLinkUserDialogOpen(false)} disabled={linkingUser}>
              Cancelar
            </Button>
            <Button 
              onClick={handleLinkUser}
              disabled={linkingUser}
              className={selectedUserId ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
            >
              {linkingUser 
                ? 'Processando...' 
                : selectedUserId 
                  ? 'Vincular Usuário' 
                  : 'Desvincular'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
